import { Fragment, FunctionComponent, memo, Ref } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { paletteHelper, spacingHelper } from '@property.com.au/ui-kit';
import { mapUIMessagePropertyTileToPropertyTileProps } from '../helpers/mapUIMessagePropertyTileToPropertyTileProps';
import { PropertyTileMessage } from './PropertyTileMessage';
import { UserActionButtons } from './UserActionMessage';
import { ClientActionButtons } from './ClientActionMessage';
import { ComparisonTable } from './ComparisonTable';
import {
  AgentPart,
  Message as MessageType,
  UiPart,
  ToolPart,
  AgentMessage,
} from '../hooks/clientMessages';
import { LoadingDots } from '../../../../../../app-kit/components/LoadingDots/LoadingDots';
import { PropertyCarouselSelector } from './PropertyCarouselSelector';
import { mapUIMessagePropertySelectorTileToProps } from '../helpers/mapUIMessagePropertySelectorTileToProps';
import { PropertyCarouselV2 } from '../../../../../../app-kit/components/PropertyCarousel/PropertyCarouselV2';
import MarkdownRenderer from './MarkdownRenderer';

const StyledPropertyCarousel = styled(PropertyCarouselV2)`
  width: 100%;
  padding: 0;

  .carouselList {
    padding-bottom: 0;
  }
  .carouselList::before,
  .carouselList::after {
    padding-left: ${spacingHelper('medium')};
  }

  .leftScrollButton {
    left: 1rem;
    z-index: 5;
    filter: drop-shadow(0 9px 30px rgb(0 0 0 / 25%));
  }
  .rightScrollButton {
    right: 1rem;
    z-index: 5;
    filter: drop-shadow(0 9px 30px rgb(0 0 0 / 25%));
  }
`;

const MessageItem = styled.div<{ lastMessage?: boolean; offset?: number }>`
  padding-top: ${spacingHelper('medium')};
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ lastMessage, offset = 0 }) =>
    lastMessage &&
    css`
      flex: 1 0 calc(100% - ${offset}px);
    `}

  > *:not(:first-child) {
    padding-top: ${spacingHelper('small')};
  }
`;

const UserMessageWrapper = styled.div`
  align-self: flex-end;
  padding: ${spacingHelper('small large')};
  border-radius: 26px;
  background: ${paletteHelper('backgroundSecondaryLow')};
  color: ${paletteHelper('textPrimary')};
  white-space: pre-wrap;
  margin-right: ${spacingHelper('medium')};
  max-width: 90%;
`;

const TextComponentWrapper = styled.div`
  margin: ${spacingHelper('0 medium')};
`;

const LoadingDotsWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: ${spacingHelper('medium')};
  margin-top: ${spacingHelper('small')};
  padding-left: ${spacingHelper('medium')};
  margin-bottom: ${spacingHelper('medium')};
`;

const shimmerAnimation = keyframes`
  0% { background-position: 150% 0; }
  100% { background-position: -150% 0; }
`;

const fadeInAnimation = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const LoadingMessageText = styled.div`
  color: ${paletteHelper('textSecondary')};
  background-image: linear-gradient(
    90deg,
    ${paletteHelper('textSecondary')} 0%,
    ${paletteHelper('textPrimary')} 25%,
    ${paletteHelper('textInverted')} 50%,
    ${paletteHelper('textPrimary')} 75%,
    ${paletteHelper('textSecondary')} 100%
  );
  background-size: 300% 100%;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation:
    ${shimmerAnimation} 5s linear infinite,
    ${fadeInAnimation} 0.5s ease-out forwards;
`;

const isUiPart = (part: AgentPart): part is UiPart => part.type === 'content';
const isToolPart = (part: AgentPart): part is ToolPart => part.type === 'tool_call';

interface LoadingViewProps {
  message: AgentMessage;
}

const LoadingView: FunctionComponent<LoadingViewProps> = ({ message }) => {
  let currentLoadingText = 'Analysing your request';
  const toolParts = message.parts.filter(isToolPart);

  if (toolParts.length > 0) {
    const active = [...toolParts].reverse().find((p) => !p.finished) || null;
    if (active) {
      switch (active.tool) {
        case 'match_multiple_addresses_and_get_property_details_tool':
          currentLoadingText = 'Gathering property details';
          break;
        case 'get_similar_on_market_properties_tool':
          currentLoadingText = 'Finding similar properties';
          break;
        case 'get_listing_data_tool':
          currentLoadingText = 'Retrieving listing data';
          break;
        default:
          currentLoadingText = 'Processing your request';
      }
    } else {
      currentLoadingText = 'Finalising response';
    }
  }

  return (
    <LoadingDotsWrapper>
      <LoadingMessageText key={currentLoadingText}>{currentLoadingText}</LoadingMessageText>
      <LoadingDots />
    </LoadingDotsWrapper>
  );
};

interface MessageProps {
  message: MessageType;
  appendChat: (text: string, visibility: 'visible' | 'hidden') => Promise<void>;
  lastMessage: boolean;
  loading: boolean;
  offset?: number;
  userInteractionRef?: Ref<HTMLDivElement>;
}

export const PureMessage: FunctionComponent<MessageProps> = ({
  message,
  appendChat,
  lastMessage = false,
  loading,
  userInteractionRef,
  offset,
}) => {
  if (message.sender === 'agent') {
    const uiParts = message.parts.filter(isUiPart);

    return (
      <MessageItem lastMessage={lastMessage} offset={offset}>
        {uiParts.map((part, partIndex) => (
          <Fragment key={partIndex}>
            {part.components.map((component, componentIndex) => {
              const key = `${partIndex}-${componentIndex}`;
              switch (component.componentType) {
                case 'text':
                  return (
                    <TextComponentWrapper key={key}>
                      <MarkdownRenderer>{component.text}</MarkdownRenderer>
                    </TextComponentWrapper>
                  );
                case 'property_tile':
                  return <PropertyTileMessage key={key} component={component} />;
                case 'property_tiles':
                  return (
                    <StyledPropertyCarousel
                      key={key}
                      properties={component.properties.map(
                        mapUIMessagePropertyTileToPropertyTileProps,
                      )}
                    />
                  );
                case 'user_action':
                  return (
                    <UserActionButtons
                      userInteractionRef={userInteractionRef}
                      key={key}
                      actions={component.actions}
                      onActionClick={(action) =>
                        void appendChat(`User action - ${action}`, 'visible')
                      }
                    />
                  );
                case 'client_action':
                  return (
                    <ClientActionButtons
                      userInteractionRef={userInteractionRef}
                      key={key}
                      actions={component.actions}
                      onActionClick={(action) => void appendChat(action.agentMessage, 'visible')}
                    />
                  );
                case 'comparison_table':
                  return <ComparisonTable key={key} component={component} />;
                case 'property_selector':
                  return (
                    <PropertyCarouselSelector
                      userInteractionRef={userInteractionRef}
                      key={key}
                      properties={component.properties.map(mapUIMessagePropertySelectorTileToProps)}
                      actions={component.actions}
                      appendChat={appendChat}
                    />
                  );
                default:
                  return null;
              }
            })}
          </Fragment>
        ))}
        {loading && lastMessage && <LoadingView message={message} />}
      </MessageItem>
    );
  }

  if (message.sender === 'user') {
    return (
      <MessageItem ref={userInteractionRef}>
        <UserMessageWrapper>{message.text}</UserMessageWrapper>
      </MessageItem>
    );
  }

  return null;
};

export const Message = memo(PureMessage);
